import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, Eye, Gauge, TrendingUp } from "lucide-react";
import { scrollToId } from "@/config";

const HERO_IMG =
  "https://images.unsplash.com/photo-1651761179569-4ba2aa054997?crop=entropy&cs=srgb&fm=jpg&q=85";

const lines = [
  { text: "Sviluppo siti web", cls: "text-zinc-50" },
  { text: "ad alto impatto", cls: "bg-gradient-to-r from-cyan-400 via-sky-500 to-indigo-500 bg-clip-text text-transparent" },
  { text: "per ogni attività.", cls: "text-zinc-50" },
];

const MaskedLine = ({ text, cls, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`block ${cls}`}
    >
      {text}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const rotateX = useTransform(sy, [-0.5, 0.5], [9, -9]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-11, 11]);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative min-h-screen flex items-center overflow-hidden pt-32 pb-20"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-x-0 top-0 h-[90%]"
          style={{
            background:
              "linear-gradient(to bottom, rgba(56,189,248,0.60) 0%, rgba(56,189,248,0.30) 26%, rgba(79,124,255,0.12) 50%, rgba(9,9,11,0) 82%)",
          }}
          data-testid="hero-gradient-bg"
        />
        <div className="absolute -top-40 -left-40 h-[34rem] w-[34rem] rounded-full bg-cyan-400/15 blur-[140px]" />
        <div className="absolute bottom-0 right-0 h-[28rem] w-[28rem] rounded-full bg-indigo-500/10 blur-[140px]" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <h1 className="font-syne text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08]">
            <MaskedLine text={lines[0].text} cls={lines[0].cls} delay={0.3} />
            <MaskedLine text={lines[1].text} cls={lines[1].cls} delay={0.45} />
            <MaskedLine text={lines[2].text} cls={lines[2].cls} delay={0.6} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-7 max-w-lg text-base sm:text-lg text-zinc-400 leading-relaxed"
          >
            Landing page, e-commerce e siti web per qualsiasi attività o progetto.
            Design su misura, velocità estrema e un solo obiettivo: trasformare i visitatori in clienti.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("#contatti")}
              className="group inline-flex items-center gap-2 rounded-full bg-cyan-400 px-7 py-3.5 font-semibold text-zinc-950 transition-all duration-300 hover:bg-cyan-300 hover:shadow-[0_0_36px_rgba(56,189,248,0.45)]"
              data-testid="hero-primary-cta"
            >
              Richiedi Preventivo su Misura
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              onClick={() => scrollToId("#portfolio")}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 font-semibold text-zinc-200 transition-all duration-300 hover:border-cyan-400/50 hover:text-cyan-300"
              data-testid="hero-secondary-cta"
            >
              <Eye size={18} />
              Guarda i Lavori
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          style={{ perspective: 1200 }}
          className="relative hidden lg:block"
        >
          <motion.div
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="relative rounded-2xl border border-white/10 bg-zinc-900/60 backdrop-blur-md shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)] overflow-hidden"
            data-testid="hero-device-frame"
          >
            <div className="flex items-center gap-1.5 border-b border-white/10 px-4 py-3">
              <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
              <span className="h-2.5 w-2.5 rounded-full bg-zinc-700" />
              <span className="h-2.5 w-2.5 rounded-full bg-cyan-400" />
              <span className="ml-3 flex-1 rounded-md bg-zinc-800/80 px-3 py-1 font-mono text-[10px] text-zinc-500">
                mpdigital.it/il-tuo-sito
              </span>
            </div>
            <img src={HERO_IMG} alt="Anteprima sito web realizzato da Mp Digital" className="h-[380px] w-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-zinc-950/90 to-transparent" />
          </motion.div>

          <motion.div
            style={{ transform: "translateZ(60px)" }}
            className="absolute -left-8 top-16 animate-float-slow rounded-xl border border-white/10 bg-zinc-900/80 backdrop-blur-md px-4 py-3 shadow-2xl"
            data-testid="hero-badge-lighthouse"
          >
            <div className="flex items-center gap-2.5">
              <Gauge size={18} className="text-cyan-400" />
              <div>
                <p className="font-syne text-lg font-bold leading-none">100<span className="text-cyan-400">/100</span></p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Lighthouse</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            style={{ transform: "translateZ(80px)", animationDelay: "1.5s" }}
            className="absolute -right-6 bottom-14 animate-float-slow rounded-xl border border-white/10 bg-zinc-900/80 backdrop-blur-md px-4 py-3 shadow-2xl"
            data-testid="hero-badge-growth"
          >
            <div className="flex items-center gap-2.5">
              <TrendingUp size={18} className="text-amber-400" />
              <div>
                <p className="font-syne text-lg font-bold leading-none">+38%</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Prenotazioni online</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
